import React, { useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { agentService } from '../services/api';

const AgentModal = ({ show, onHide, agent, groupes }) => {
  const queryClient = useQueryClient();
  const isEditing = !!agent;

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      nom: '',
      prenoms: '',
      matricule: '',
      fonction: '',
      groupeId: '',
      actif: true
    }
  });

  // Mutations
  const createMutation = useMutation(agentService.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('agents');
      onHide();
      reset();
    }
  });

  const updateMutation = useMutation(
    ({ id, data }) => agentService.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('agents');
        onHide();
      }
    }
  );

  // Effet pour réinitialiser le formulaire quand l'agent change
  useEffect(() => {
    if (agent) {
      reset({
        nom: agent.nom || '',
        prenoms: agent.prenoms || '',
        matricule: agent.matricule || '',
        fonction: agent.fonction || '',
        groupeId: agent.groupeId || '',
        actif: agent.actif !== undefined ? agent.actif : true
      });
    } else {
      reset({
        nom: '',
        prenoms: '',
        matricule: '',
        fonction: '',
        groupeId: '',
        actif: true
      });
    }
  }, [agent, reset]);

  const onSubmit = async (data) => {
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: agent.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (error) {
      // L'erreur sera affichée via les mutations
      console.error('Erreur:', error);
    }
  };

  const handleClose = () => {
    onHide();
    reset();
  };

  const mutation = isEditing ? updateMutation : createMutation;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? 'Modifier l\'agent' : 'Nouvel agent'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          {mutation.error && (
            <Alert variant="danger">
              Erreur: {mutation.error.response?.data || mutation.error.message}
            </Alert>
          )}

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Nom *</Form.Label>
                <Form.Control
                  type="text"
                  {...register('nom', { 
                    required: 'Le nom est obligatoire',
                    minLength: { value: 2, message: 'Le nom doit faire au moins 2 caractères' }
                  })}
                  isInvalid={!!errors.nom}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.nom?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Prénoms *</Form.Label>
                <Form.Control
                  type="text"
                  {...register('prenoms', { 
                    required: 'Les prénoms sont obligatoires',
                    minLength: { value: 2, message: 'Les prénoms doivent faire au moins 2 caractères' }
                  })}
                  isInvalid={!!errors.prenoms}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.prenoms?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Matricule</Form.Label>
                <Form.Control
                  type="text"
                  {...register('matricule')}
                  placeholder="Optionnel"
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Fonction</Form.Label>
                <Form.Control
                  type="text"
                  {...register('fonction')}
                  placeholder="Ex: Développeur, Gestionnaire..."
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-8">
              <Form.Group className="mb-3">
                <Form.Label>Groupe</Form.Label>
                <Form.Select {...register('groupeId')}>
                  <option value="">Sélectionner un groupe (optionnel)</option>
                  {groupes.map(groupe => (
                    <option key={groupe.id} value={groupe.id}>
                      {groupe.nom}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Statut</Form.Label>
                <Form.Select {...register('actif')}>
                  <option value={true}>Actif</option>
                  <option value={false}>Inactif</option>
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          {isEditing && (
            <Alert variant="info">
              <strong>Note:</strong> Pour modifier la photo de cet agent, utilisez le bouton "Gérer la photo" dans le tableau.
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Annuler
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? 'Sauvegarde...' : (isEditing ? 'Modifier' : 'Créer')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AgentModal; 